/**
 * AI Prompt Panel Component - POLISHED FOR EXCELLENCE
 * 
 * Features: 
 * - Progressive loading states
 * - Comprehensive error handling
 * - Success feedback animations
 * - Smart prompt suggestions
 * - Response explanations
 */

import React, { useState, useEffect, useRef } from 'react';
import { useCanvas } from '../../hooks/useCanvas';
import { useAuth } from '../../hooks/useAuth';
import { useAIActivity } from '../../hooks/useAIActivity';
import { generateShapesFromPrompt } from '../../services/ai';
import { setAIActive } from '../../services/aiPresence';

const EXAMPLE_PROMPTS = [
  "Create a login form with email and password fields",
  "Make a blue circle and red square",
  "Design a navigation bar with 4 menu items",
  "Create a 3x3 grid of squares",
  "Build a card layout with title and description",
  "Add a button that says 'Get Started'",
  "Create a dashboard with sidebar",
  "Make a 4x4 grid of circles",
  "Design a hero section with title",
  "Create a profile card"
];

const ERROR_MESSAGES = {
  network: "Couldn't connect to AI. Check your internet connection.",
  timeout: "AI is taking too long. Try a simpler prompt like 'Create a blue circle'.",
  invalid: "AI couldn't understand that. Try rephrasing your request.",
  rate_limit: "Too many requests. Please wait a moment and try again.",
  api: "AI service is temporarily unavailable. Please try again in a moment.",
  validation: "That prompt doesn't look right. Try: 'Create a red square'",
  database: "Couldn't save shapes to canvas. Check your connection.",
  general: "Something went wrong. Please try again."
};

const MAX_PROMPT_LENGTH = 500;
const MAX_RECENT_PROMPTS = 5;
const AI_TIMEOUT = 30000; // 30 seconds

// Loading stage messages
const LOADING_MESSAGES = [
  { duration: 0, message: "Understanding your request...", icon: "🤔" },
  { duration: 1000, message: "Generating shapes...", icon: "🎨" },
  { duration: 2000, message: "Almost done...", icon: "✨" },
  { duration: 3000, message: "AI is working on something complex...", icon: "🔮" }
];

export default function AIPromptPanel() {
  // State
  const [isExpanded, setIsExpanded] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const [aiResponse, setAiResponse] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(true);
  const [recentPrompts, setRecentPrompts] = useState([]);
  const [aiToast, setAiToast] = useState(null);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [rotatingPromptIndex, setRotatingPromptIndex] = useState(0);
  const [lastAttemptedPrompt, setLastAttemptedPrompt] = useState('');
  
  const textareaRef = useRef(null);
  const timeoutRef = useRef(null);
  const loadingTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  const { currentUser } = useAuth();
  const { shapes, addAIShapes, previewShapes, setPreviewShapes } = useCanvas();
  const othersAIActivity = useAIActivity();

  // Load recent prompts from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('aiRecentPrompts');
      if (stored) {
        setRecentPrompts(JSON.parse(stored));
      }
    } catch (err) {
      console.error('Failed to load recent prompts:', err);
    }
  }, []);

  // Update suggested prompts based on canvas state
  useEffect(() => {
    if (shapes.length === 0) {
      setSuggestedPrompts([
        "Get started: Create a welcome screen",
        "Try: Add a header and footer",
        "Start with: Create a simple form"
      ]);
    } else {
      setSuggestedPrompts([
        "Try: Arrange these in a grid",
        "Add: Create a navigation menu",
        "Enhance: Add some buttons"
      ]);
    }
  }, [shapes.length]);

  // Rotate example prompts every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setRotatingPromptIndex((prev) => (prev + 1) % EXAMPLE_PROMPTS.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [prompt]);

  // Loading stage progression
  useEffect(() => {
    if (isLoading) {
      let stage = 0;
      
      const timers = LOADING_MESSAGES.map((msg, index) => {
        return setTimeout(() => {
          setLoadingStage(index);
        }, msg.duration);
      });
      
      loadingTimerRef.current = timers;
      
      return () => {
        timers.forEach(clearTimeout);
      };
    } else {
      setLoadingStage(0);
    }
  }, [isLoading]);

  // Handle prompt submission with timeout and cancellation
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!prompt.trim() || isLoading) return;
    
    const currentPrompt = prompt.trim();
    
    // Validate prompt
    if (currentPrompt.length < 3) {
      setError({
        type: 'validation',
        message: ERROR_MESSAGES.validation
      });
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setAiResponse(null);
    setSuccess(null);
    setPreviewShapes([]);
    setLastAttemptedPrompt(currentPrompt);

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    // Set AI activity status
    if (currentUser) {
      setAIActive(currentUser.uid, true, currentPrompt);
    }

    // Set timeout
    timeoutRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        setError({
          type: 'timeout',
          message: ERROR_MESSAGES.timeout
        });
        setIsLoading(false);
        if (currentUser) {
          setAIActive(currentUser.uid, false);
        }
      }
    }, AI_TIMEOUT);

    try {
      const startTime = Date.now();
      const result = await generateShapesFromPrompt(currentPrompt, shapes);
      const duration = Date.now() - startTime;
      
      if (result.success) {
        setAiResponse(result);
        saveRecentPrompt(currentPrompt);
        
        // Show success message
        setSuccess({
          message: `✨ Created ${result.shapes.length} shape${result.shapes.length !== 1 ? 's' : ''} from your prompt`,
          explanation: result.explanation || 'Shapes generated successfully',
          duration: Math.round(duration / 1000) + 's'
        });
        
        if (isPreviewMode) {
          // Show preview
          setPreviewShapes(result.shapes);
        } else {
          // Add shapes directly
          await addShapesToCanvas(result.shapes, currentPrompt);
          setPrompt('');
          
          // Auto-collapse after success
          setTimeout(() => {
            setIsExpanded(false);
            setAiResponse(null);
            setSuccess(null);
          }, 3000);
        }
      } else {
        handleError(result.error || 'Failed to generate shapes');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // Request was cancelled
        return;
      }
      handleError(err);
    } finally {
      setIsLoading(false);
      clearTimeout(timeoutRef.current);
      if (currentUser) {
        setAIActive(currentUser.uid, false);
      }
      abortControllerRef.current = null;
    }
  };

  // Cancel AI request
  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    clearTimeout(timeoutRef.current);
    setIsLoading(false);
    setLoadingStage(0);
    if (currentUser) {
      setAIActive(currentUser.uid, false);
    }
  };

  // Enhanced error handling with better messages
  const handleError = (err) => {
    const errorMessage = err?.message || err?.toString() || 'Unknown error';
    
    let errorType = 'general';
    
    // Determine error type
    if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
      errorType = 'network';
    } else if (errorMessage.includes('429') || errorMessage.includes('rate') || errorMessage.includes('quota')) {
      errorType = 'rate_limit';
    } else if (errorMessage.includes('API') || errorMessage.includes('OpenAI') || errorMessage.includes('503')) {
      errorType = 'api';
    } else if (errorMessage.includes('invalid') || errorMessage.includes('parse') || errorMessage.includes('400')) {
      errorType = 'invalid';
    } else if (errorMessage.includes('timeout')) {
      errorType = 'timeout';
    }
    
    setError({
      type: errorType,
      message: ERROR_MESSAGES[errorType] || ERROR_MESSAGES.general
    });
  };

  // Retry last prompt
  const handleRetry = () => {
    if (lastAttemptedPrompt) {
      setPrompt(lastAttemptedPrompt);
      setError(null);
      setTimeout(() => handleSubmit(), 100);
    }
  };

  // Add shapes to canvas with AI metadata
  const addShapesToCanvas = async (shapesToAdd, aiPrompt = null) => {
    try {
      const result = await addAIShapes(shapesToAdd, aiPrompt);
      if (result.success) {
        console.log(`✨ Successfully added ${result.successCount} AI shapes to canvas`);
      } else {
        console.error('Failed to add AI shapes:', result.error);
        setError({
          type: 'database',
          message: 'Could not save shapes to canvas.',
          suggestion: 'Please check your connection and try again'
        });
      }
      return result;
    } catch (error) {
      console.error('Error adding AI shapes:', error);
      setError({
        type: 'database',
        message: 'Failed to add shapes.',
        suggestion: 'Please try again'
      });
      return { success: false, error: error.message };
    }
  };

  // Handle accept preview
  const handleAccept = async () => {
    if (previewShapes.length === 0) return;
    
    setIsLoading(true);
    const originalPrompt = aiResponse?.explanation || prompt;
    await addShapesToCanvas(previewShapes, originalPrompt);
    setIsLoading(false);
    
    setPreviewShapes([]);
    setAiResponse(null);
    setSuccess(null);
    setPrompt('');
    setIsExpanded(false);
  };

  // Handle reject preview
  const handleReject = () => {
    setPreviewShapes([]);
    setAiResponse(null);
    setSuccess(null);
  };

  // Handle regenerate
  const handleRegenerate = () => {
    handleReject();
    handleSubmit();
  };

  // Save prompt to recent history
  const saveRecentPrompt = (newPrompt) => {
    try {
      const updated = [newPrompt, ...recentPrompts.filter(p => p !== newPrompt)].slice(0, MAX_RECENT_PROMPTS);
      setRecentPrompts(updated);
      localStorage.setItem('aiRecentPrompts', JSON.stringify(updated));
    } catch (err) {
      console.error('Failed to save recent prompt:', err);
    }
  };

  // Toggle panel expansion
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 300);
    }
  };

  // Use example or recent prompt
  const usePrompt = (promptText) => {
    setPrompt(promptText);
    textareaRef.current?.focus();
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      if (isLoading) {
        handleCancel();
      } else {
        setIsExpanded(false);
      }
    }
  };

  const currentLoadingMessage = LOADING_MESSAGES[loadingStage];

  return (
    <>
      {/* Preview Shapes Overlay */}
      {previewShapes.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-40">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" />
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-full shadow-lg font-medium animate-bounce-subtle">
            👀 Preview Mode - {previewShapes.length} shape(s)
          </div>
        </div>
      )}

      {/* Other Users' AI Activity Indicators */}
      {othersAIActivity.length > 0 && (
        <div className="fixed top-20 right-4 z-50 space-y-2">
          {othersAIActivity.map((activity) => (
            <div
              key={activity.userId}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-pulse"
              style={{ borderLeftColor: activity.cursorColor, borderLeftWidth: '4px' }}
            >
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm font-medium">
                {activity.displayName} is asking AI...
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Success Toast */}
      {success && !isLoading && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 max-w-md">
            <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div className="flex-1">
              <p className="font-semibold">{success.message}</p>
              {success.explanation && (
                <p className="text-sm text-white/90 mt-1">{success.explanation}</p>
              )}
              {success.duration && (
                <p className="text-xs text-white/75 mt-1">Generated in {success.duration}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Toast with Retry */}
      {error && !isLoading && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-shake">
          <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white px-6 py-4 rounded-lg shadow-2xl max-w-md">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="font-semibold">{error.message}</p>
                <div className="flex items-center gap-2 mt-3">
                  {lastAttemptedPrompt && (
                    <button
                      onClick={handleRetry}
                      className="text-sm bg-white/20 hover:bg-white/30 px-4 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Try Again
                    </button>
                  )}
                  <button
                    onClick={() => setError(null)}
                    className="text-sm text-white/75 hover:text-white underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Panel */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 transition-all duration-300">
        {!isExpanded ? (
          /* Collapsed State */
          <button
            onClick={toggleExpand}
            className="group relative bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white px-8 py-4 rounded-full shadow-2xl hover:shadow-indigo-500/50 transition-all duration-300 hover:scale-105 flex items-center gap-3 animate-float"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 7H7v6h6V7z" />
              <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
            </svg>
            <span className="font-semibold text-lg">Ask AI ✨</span>
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
          </button>
        ) : (
          /* Expanded State */
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 w-[600px] max-w-[90vw] animate-scale-up">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200/50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 7H7v6h6V7z" />
                    <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">AI Canvas Assistant</h3>
                  <p className="text-xs text-gray-500">Describe what you want to create</p>
                </div>
              </div>
              <button
                onClick={toggleExpand}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Smart Suggestions */}
              {suggestedPrompts.length > 0 && !aiResponse && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">💡 Suggestions</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedPrompts.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => usePrompt(suggestion.replace(/^(Get started|Try|Start with|Add|Enhance): /, ''))}
                        className="text-xs px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-indigo-700 rounded-full hover:from-blue-100 hover:to-indigo-100 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Textarea with Character Counter */}
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="E.g., 'Create a login form' or 'Add 3 blue circles in a row'"
                  className={`w-full px-4 py-3 rounded-xl border-2 focus:ring-4 outline-none transition-all resize-none min-h-[80px] max-h-[200px] ${
                    prompt.length > 450
                      ? 'border-orange-400 focus:border-orange-500 focus:ring-orange-500/20'
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20'
                  }`}
                  disabled={isLoading}
                  maxLength={MAX_PROMPT_LENGTH}
                />
                <div className={`absolute bottom-2 right-2 text-xs font-medium transition-colors ${
                  prompt.length > 450
                    ? 'text-orange-600'
                    : prompt.length > 400
                    ? 'text-yellow-600'
                    : 'text-gray-400'
                }`}>
                  {prompt.length}/{MAX_PROMPT_LENGTH}
                  {prompt.length > 450 && ' ⚠️'}
                </div>
              </div>

              {/* Rotating Example Prompts */}
              {!aiResponse && !isLoading && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">💭 Try this</p>
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 border border-indigo-100">
                    <button
                      onClick={() => usePrompt(EXAMPLE_PROMPTS[rotatingPromptIndex])}
                      className="w-full text-left text-sm text-indigo-900 font-medium hover:text-indigo-700 transition-colors flex items-center justify-between group"
                    >
                      <span className="flex-1">"{EXAMPLE_PROMPTS[rotatingPromptIndex]}"</span>
                      <svg className="w-4 h-4 text-indigo-400 group-hover:text-indigo-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Suggestions update every 5 seconds
                    </p>
                  </div>
                  
                  {/* Quick Examples Grid */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {EXAMPLE_PROMPTS.slice(rotatingPromptIndex + 1, rotatingPromptIndex + 3).map((example, index) => (
                      <button
                        key={index}
                        onClick={() => usePrompt(example)}
                        className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                      >
                        {example.length > 30 ? example.substring(0, 30) + '...' : example}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Prompts */}
              {!aiResponse && recentPrompts.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">🕐 Recent</p>
                  <div className="space-y-1">
                    {recentPrompts.slice(0, 3).map((recent, index) => (
                      <button
                        key={index}
                        onClick={() => usePrompt(recent)}
                        className="w-full text-left text-sm px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors truncate"
                      >
                        {recent}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-indigo-900">
                        {currentLoadingMessage.icon} {currentLoadingMessage.message}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCancel}
                    className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                  >
                    Cancel request
                  </button>
                </div>
              )}

              {/* AI Response */}
              {aiResponse && !isLoading && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-900">
                        {aiResponse.explanation || 'Shapes generated successfully!'}
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Generated {aiResponse.shapes?.length || 0} shape{(aiResponse.shapes?.length || 0) !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Mode Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">Preview before adding</span>
                </div>
                <button
                  onClick={() => setIsPreviewMode(!isPreviewMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isPreviewMode ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isPreviewMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t border-gray-200/50 bg-gray-50/50 rounded-b-2xl">
              {previewShapes.length > 0 ? (
                /* Preview Actions */
                <div className="flex gap-2">
                  <button
                    onClick={handleAccept}
                    disabled={isLoading}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-3 rounded-xl font-medium hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ✓ Accept & Add
                  </button>
                  <button
                    onClick={handleRegenerate}
                    disabled={isLoading}
                    className="px-4 py-3 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors border border-gray-300 disabled:opacity-50"
                  >
                    🔄 Regenerate
                  </button>
                  <button
                    onClick={handleReject}
                    className="px-4 py-3 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors border border-gray-300"
                  >
                    ✕ Cancel
                  </button>
                </div>
              ) : (
                /* Generate Action */
                <button
                  onClick={handleSubmit}
                  disabled={isLoading || !prompt.trim()}
                  className="w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 7H7v6h6V7z" />
                        <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                      </svg>
                      Generate Shapes
                      <span className="text-xs opacity-75">(⌘↩)</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes scale-up {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes slide-down {
          from { transform: translate(-50%, -20px); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
        @keyframes shake {
          0%, 100% { transform: translate(-50%, 0); }
          10%, 30%, 50%, 70%, 90% { transform: translate(-48%, 0); }
          20%, 40%, 60%, 80% { transform: translate(-52%, 0); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-scale-up {
          animation: scale-up 0.2s ease-out;
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
        .animate-shake {
          animation: shake 0.5s ease-out;
        }
        .animate-bounce-subtle {
          animation: bounce 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}
